import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../services/apiClient';
import './TabStyles.css';
import './HumanInTheLoopTab.css';

const HumanInTheLoopTab = ({ candidate }) => {
    // State for Decision Control
    const [finalDecision, setFinalDecision] = useState(null);
    const [decisionJustification, setDecisionJustification] = useState('');
    const [isDecisionLocked, setIsDecisionLocked] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    // State for Score Override
    const [aiScore, setAiScore] = useState(0);
    const [adjustedScore, setAdjustedScore] = useState(0);
    const [adjustmentReason, setAdjustmentReason] = useState('');
    const [adjustmentHistory, setAdjustmentHistory] = useState([]);

    // State for Human Notes
    const [strengths, setStrengths] = useState('');
    const [concerns, setConcerns] = useState('');
    const [interviewSignals, setInterviewSignals] = useState('');
    const [teamFeedback, setTeamFeedback] = useState('');

    // State for Governance Metadata
    const [reviewerName, setReviewerName] = useState('');
    const [reviewerRole, setReviewerRole] = useState('');
    const [decisionConfidence, setDecisionConfidence] = useState('');

    // Load existing data
    useEffect(() => {
        const loadData = async () => {
            try {
                // Get AI score
                const ragAnalysis = typeof candidate.rag_analysis === 'string' 
                    ? JSON.parse(candidate.rag_analysis || '{}') 
                    : (candidate.rag_analysis || {});
                const evalResult = ragAnalysis?.evaluation_result || {};
                const score = evalResult.calibrated_score || evalResult.overall_score || evalResult.fit_score || 0;
                setAiScore(score);
                setAdjustedScore(score);

                // Fetch existing decision
                const decisionResponse = await apiFetch(`/api/feedback/hiring-decision/${candidate.id}`);
                if (decisionResponse.ok) {
                    const decisionData = await decisionResponse.json();
                    if (decisionData.success && decisionData.data) {
                        setFinalDecision(decisionData.data.decision);
                        setDecisionJustification(decisionData.data.justification || '');
                        setIsDecisionLocked(decisionData.data.is_locked || false);
                    }
                }

                // Fetch adjustment history
                const historyResponse = await apiFetch(`/api/feedback/score-adjustments/${candidate.id}`);
                if (historyResponse.ok) {
                    const historyData = await historyResponse.json();
                    setAdjustmentHistory(historyData.adjustments || []);
                }

                // Fetch human notes
                const notesResponse = await apiFetch(`/api/feedback/reviewer-notes/${candidate.id}`);
                if (notesResponse.ok) {
                    const notesData = await notesResponse.json();
                    if (notesData.success && notesData.data) {
                        setStrengths(notesData.data.strengths || '');
                        setConcerns(notesData.data.concerns || '');
                        setInterviewSignals(notesData.data.interview_signals || '');
                        setTeamFeedback(notesData.data.team_feedback || '');
                    }
                }

                // Fetch reviewer metadata
                const metadataResponse = await apiFetch(`/api/feedback/reviewer-metadata/${candidate.id}`);
                if (metadataResponse.ok) {
                    const metadataData = await metadataResponse.json();
                    if (metadataData.success && metadataData.data) {
                        setReviewerName(metadataData.data.reviewer_name || '');
                        setReviewerRole(metadataData.data.reviewer_role || '');
                        setDecisionConfidence(metadataData.data.decision_confidence || '');
                    }
                }

                // Check if user is admin (try to get from session or skip for now)
                // TODO: Add proper user endpoint
                setIsAdmin(false); // Default to false, can be enhanced later
            } catch (error) {
                
            }
        };

        if (candidate.id) {
            loadData();
        }
    }, [candidate.id]);

    // Handle final decision submission
    const handleFinalDecision = async (decision) => {
        if (isDecisionLocked && !isAdmin) {
            alert('This decision has been finalized and cannot be changed.');
            return;
        }

        if (!decisionJustification.trim()) {
            alert('Justification is required for final decisions.');
            return;
        }

        try {
            const response = await apiFetch('/api/feedback/final-decision', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    resume_id: candidate.id,
                    decision: decision,
                    justification: decisionJustification,
                    lock_decision: true
                })
            });

            const data = await response.json();
            if (data.success) {
                setFinalDecision(decision);
                setIsDecisionLocked(true);
                alert('Decision finalized successfully.');
            } else {
                alert(data.error || 'Failed to save decision');
            }
        } catch (error) {
            
            alert('Failed to save decision');
        }
    };

    // Handle score adjustment
    const handleScoreAdjustment = async () => {
        const adjustmentDelta = Math.abs(adjustedScore - aiScore);
        
        if (adjustmentDelta > 10) {
            alert('Adjustment range is capped at ±10 points.');
            return;
        }

        if (!adjustmentReason.trim()) {
            alert('Reason for adjustment is required.');
            return;
        }

        try {
            const response = await apiFetch('/api/feedback/adjust-score', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    resume_id: candidate.id,
                    original_score: aiScore,
                    adjusted_score: adjustedScore,
                    dimension: 'overall',
                    reasoning: adjustmentReason,
                    comment: adjustmentReason
                })
            });

            const data = await response.json();
            if (data.success) {
                setAdjustmentHistory(prev => [data.data, ...prev]);
                setAdjustmentReason('');
                alert('Score adjustment saved.');
            } else {
                alert(data.error || 'Failed to save adjustment');
            }
        } catch (error) {
            
            alert('Failed to save adjustment');
        }
    };

    // Save human notes
    const handleSaveNotes = async () => {
        try {
            const response = await apiFetch('/api/feedback/reviewer-notes', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    resume_id: candidate.id,
                    strengths: strengths,
                    concerns: concerns,
                    interview_signals: interviewSignals,
                    team_feedback: teamFeedback
                })
            });

            const data = await response.json();
            if (data.success) {
                // Also save metadata if reviewer info is filled
                if (reviewerName || reviewerRole || decisionConfidence) {
                    await apiFetch('/api/feedback/reviewer-metadata', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            resume_id: candidate.id,
                            reviewer_name: reviewerName,
                            reviewer_role: reviewerRole,
                            decision_confidence: decisionConfidence
                        })
                    });
                }
                alert('Notes saved successfully.');
            } else {
                alert(data.error || 'Failed to save notes');
            }
        } catch (error) {
            
            alert('Failed to save notes');
        }
    };

    const adjustmentDelta = adjustedScore - aiScore;
    const canAdjust = Math.abs(adjustmentDelta) <= 10;

    return (
        <div className="tab-content human-in-the-loop-tab">
            <h2 className="section-header">HUMAN IN THE LOOP</h2>
            <p className="section-description" style={{ marginTop: '-1rem', marginBottom: '2rem', color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
                Decision cockpit for final hiring decisions, score adjustments, and reviewer notes. All actions are logged for audit and compliance.
            </p>

            {/* A. Decision Control */}
            <div className="decision-cockpit-section decision-control-section">
                <h3 className="section-header">FINAL HIRING DECISION</h3>
                <div className="decision-buttons">
                    <button
                        className={`decision-btn decision-hire ${finalDecision === 'hire' ? 'selected' : ''}`}
                        onClick={() => setFinalDecision('hire')}
                        disabled={isDecisionLocked && !isAdmin}
                    >
                        Hire
                    </button>
                    <button
                        className={`decision-btn decision-hire-conditions ${finalDecision === 'hire_with_conditions' ? 'selected' : ''}`}
                        onClick={() => setFinalDecision('hire_with_conditions')}
                        disabled={isDecisionLocked && !isAdmin}
                    >
                        Hire with Conditions
                    </button>
                    <button
                        className={`decision-btn decision-no-hire ${finalDecision === 'no_hire' ? 'selected' : ''}`}
                        onClick={() => setFinalDecision('no_hire')}
                        disabled={isDecisionLocked && !isAdmin}
                    >
                        No Hire
                    </button>
                </div>

                {finalDecision && (
                    <div className="decision-justification-section">
                        <label htmlFor="decision-justification" className="justification-label">
                            Justification <span className="required">*</span>
                        </label>
                        <textarea
                            id="decision-justification"
                            value={decisionJustification}
                            onChange={(e) => setDecisionJustification(e.target.value)}
                            placeholder="Provide justification for this decision..."
                            rows={4}
                            className="justification-textarea"
                            disabled={isDecisionLocked && !isAdmin}
                            required
                        />
                        <button
                            className="finalize-decision-btn"
                            onClick={() => handleFinalDecision(finalDecision)}
                            disabled={!decisionJustification.trim() || (isDecisionLocked && !isAdmin)}
                        >
                            {isDecisionLocked ? 'Decision Finalized' : 'Finalize Decision'}
                        </button>
                        {isDecisionLocked && (
                            <div className="decision-locked-notice">
                                🔒 Decision has been finalized. Only administrators can modify.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* B. Score Override */}
            <div className="decision-cockpit-section score-override-section">
                <h3 className="section-header">SCORE OVERRIDE</h3>
                <div className="score-override-display">
                    <div className="score-comparison">
                        <div className="score-item">
                            <label>AI Score</label>
                            <div className="score-value ai-score">{aiScore.toFixed(1)}</div>
                        </div>
                        <div className="score-arrow">→</div>
                        <div className="score-item">
                            <label>Human Adjusted Score</label>
                            <div className={`score-value adjusted-score ${adjustmentDelta > 0 ? 'positive' : adjustmentDelta < 0 ? 'negative' : ''}`}>
                                {adjustedScore.toFixed(1)}
                                {adjustmentDelta !== 0 && (
                                    <span className="delta-badge">
                                        {adjustmentDelta > 0 ? '+' : ''}{adjustmentDelta.toFixed(1)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="score-slider-container">
                        <input
                            type="range"
                            min={Math.max(0, aiScore - 10)}
                            max={Math.min(100, aiScore + 10)}
                            step="0.5"
                            value={adjustedScore}
                            onChange={(e) => setAdjustedScore(parseFloat(e.target.value))}
                            className="score-slider"
                        />
                        <div className="slider-labels">
                            <span>{Math.max(0, aiScore - 10).toFixed(0)}</span>
                            <span className="current-value">{adjustedScore.toFixed(1)}</span>
                            <span>{Math.min(100, aiScore + 10).toFixed(0)}</span>
                        </div>
                        <div className="adjustment-range-note">
                            Adjustment range: ±10 points from AI score
                        </div>
                    </div>

                    <div className="adjustment-reason-section">
                        <label htmlFor="adjustment-reason" className="reason-label">
                            Reason for Adjustment <span className="required">*</span>
                        </label>
                        <textarea
                            id="adjustment-reason"
                            value={adjustmentReason}
                            onChange={(e) => setAdjustmentReason(e.target.value)}
                            placeholder="Explain why you are adjusting the score..."
                            rows={3}
                            className="reason-textarea"
                            required
                        />
                        <button
                            className="save-adjustment-btn"
                            onClick={handleScoreAdjustment}
                            disabled={!adjustmentReason.trim() || !canAdjust || adjustmentDelta === 0}
                        >
                            Save Adjustment
                        </button>
                    </div>
                </div>
            </div>

            {/* C. Human Notes & Signals */}
            <div className="decision-cockpit-section notes-section">
                <h3 className="section-header">REVIEWER NOTES</h3>
                <div className="notes-grid">
                    <div className="note-field">
                        <label htmlFor="strengths">Strengths Observed</label>
                        <textarea
                            id="strengths"
                            value={strengths}
                            onChange={(e) => setStrengths(e.target.value)}
                            placeholder="Key strengths observed during review..."
                            rows={3}
                            className="note-textarea"
                        />
                    </div>
                    <div className="note-field">
                        <label htmlFor="concerns">Concerns</label>
                        <textarea
                            id="concerns"
                            value={concerns}
                            onChange={(e) => setConcerns(e.target.value)}
                            placeholder="Areas of concern or risk..."
                            rows={3}
                            className="note-textarea"
                        />
                    </div>
                    <div className="note-field">
                        <label htmlFor="interview-signals">Interview Signals</label>
                        <textarea
                            id="interview-signals"
                            value={interviewSignals}
                            onChange={(e) => setInterviewSignals(e.target.value)}
                            placeholder="Signals from interviews or interactions..."
                            rows={3}
                            className="note-textarea"
                        />
                    </div>
                    <div className="note-field">
                        <label htmlFor="team-feedback">Team Feedback</label>
                        <textarea
                            id="team-feedback"
                            value={teamFeedback}
                            onChange={(e) => setTeamFeedback(e.target.value)}
                            placeholder="Feedback from team members or stakeholders..."
                            rows={3}
                            className="note-textarea"
                        />
                    </div>
                </div>
                <button className="save-notes-btn" onClick={handleSaveNotes}>
                    Save Notes
                </button>
            </div>

            {/* D. Approval & Governance Metadata */}
            <div className="decision-cockpit-section governance-section">
                <h3 className="section-header">APPROVAL & GOVERNANCE</h3>
                <div className="governance-metadata">
                    <div className="metadata-field">
                        <label>Reviewed By</label>
                        <input
                            type="text"
                            value={reviewerName}
                            onChange={(e) => setReviewerName(e.target.value)}
                            placeholder="Your name"
                            className="metadata-input"
                        />
                    </div>
                    <div className="metadata-field">
                        <label>Role</label>
                        <input
                            type="text"
                            value={reviewerRole}
                            onChange={(e) => setReviewerRole(e.target.value)}
                            placeholder="Hiring Manager, Recruiter, etc."
                            className="metadata-input"
                        />
                    </div>
                    <div className="metadata-field">
                        <label>Decision Confidence</label>
                        <select
                            value={decisionConfidence}
                            onChange={(e) => setDecisionConfidence(e.target.value)}
                            className="metadata-select"
                        >
                            <option value="">Select confidence level</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                        </select>
                    </div>
                    <div className="metadata-field">
                        <label>Timestamp</label>
                        <div className="metadata-value">{new Date().toLocaleString()}</div>
                    </div>
                </div>
            </div>

            {/* Adjustment History */}
            {adjustmentHistory.length > 0 && (
                <div className="decision-cockpit-section history-section">
                    <h3 className="section-header">ADJUSTMENT HISTORY</h3>
                    <div className="history-list">
                        {adjustmentHistory.map((adjustment, idx) => (
                            <div key={idx} className="history-item">
                                <div className="history-header">
                                    <span className="history-dimension">{adjustment.dimension || 'Overall'}</span>
                                    <span className="history-date">
                                        {new Date(adjustment.created_at).toLocaleString()}
                                    </span>
                                </div>
                                <div className="history-scores">
                                    <span className="score-change">
                                        {adjustment.original_score.toFixed(1)} → {adjustment.adjusted_score.toFixed(1)}
                                    </span>
                                    <span className={`score-delta ${adjustment.adjusted_score > adjustment.original_score ? 'positive' : 'negative'}`}>
                                        {adjustment.adjusted_score > adjustment.original_score ? '+' : ''}
                                        {(adjustment.adjusted_score - adjustment.original_score).toFixed(1)}
                                    </span>
                                </div>
                                {adjustment.reasoning && (
                                    <div className="history-reasoning">
                                        <strong>Reasoning:</strong> {adjustment.reasoning}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default HumanInTheLoopTab;
